/**
 * Implementation primitives for the Map **detection-glyph** layer
 * (REQ-UI-068; ADR-032 §4.1) — source + glyph layer + selection-highlight
 * layer, paint composition, GeoJSON conversion. Split from
 * :file:`detection-layer.tsx` so the ``.tsx`` file exports only its React
 * component (``react-refresh/only-export-components``) — the canonical
 * point-glyph layer pattern (the alert-glyph layer mirrors it).
 *
 * # Glyphs
 *
 * SAPIENT DetectionReports render as small glyphs whose **shape** is the active
 * Profile's ``map.detection.glyph`` (GLYPHS batch; REQ-UI-087; the closed
 * :data:`map/glyphs.GLYPH_IDS` vocabulary, default ``circle``) and whose
 * **colour** is the Profile's ``map.detection.single_color`` (inc6b) — its
 * ``color_mode`` is ``single`` only, so there is exactly one operator-chosen
 * colour. The glyph is a **MapLibre SDF icon** (:func:`map/glyphs.registerGlyphIcons`
 * bakes one white SDF per shape) painted on a ``symbol`` layer, tinted by the
 * colour via ``icon-color`` — so the shape is configurable while the inc6b
 * colour behaviour is unchanged. The Profile schema defaults the colour to
 * ``#22d3ee`` (== the shared design-system **detection accent**
 * ``--detection-glyph``), and a blank/absent value falls back to that token
 * (:func:`map/marker-styling.getDetectionGlyphColor`), so an unconfigured map
 * paints exactly the cyan it did pre-inc6b. The cyan default is semantically
 * distinct from every ``--status-*`` hue and from the alert glyph (REQ-UI-046),
 * so a detection ping is never confused with a platform health state. A flat
 * fill (not a confidence-scaled ramp) keeps the glyph legible per CLAUDE.md
 * §8.1 — ``detection_confidence`` is surfaced in the Detections panel +
 * Entity Details, not encoded into the map glyph size/opacity.
 *
 * # Null-position detections anchor at the reporting platform (REQ-UI-071)
 *
 * Detection position is **nullable** (REQ-INGEST-044): an unprojectable BSI
 * range/bearing yields ``null`` lat/lng — a *normal* state (the connector
 * returns ``None`` rather than ``(0, 0)`` when it can't project against the
 * reporting platform's cached ``node_location``). Rather than vanish, such a
 * detection is **anchored at its reporting platform's marker** when that
 * platform's position is known to the map (REQ-UI-071): the
 * ``platformPositions`` index (``producer_id`` → ``[lng, lat]``, derived from
 * the viewport-scoped snapshot in :file:`Map.tsx`) supplies the anchor. The
 * anchored glyph carries an ``anchored: true`` feature property and paints
 * **fainter** (a data-driven ``icon-opacity``; :data:`DETECTION_ANCHORED_GLYPH_OPACITY`)
 * so an operator reads it as an *approximate* position rather than a precise
 * fix (CLAUDE.md §8.1 operator-visibility — the at-a-glance "approximate"
 * affordance is intentionally provisional pending the §5.11 visual pass, which
 * may strengthen it to a halo/dashed ring; the Entity Details GEOMETRY section
 * states the anchoring in words alongside).
 *
 * When the reporting platform's position is **not** known to the map (it
 * reported no ``node_location`` of its own, so it has no marker — e.g., the
 * aggregator-omits-node_location case the connector diagnostic in
 * ``meridian_sapient_ingress`` warns on), there is nothing to anchor to and
 * the detection stays **list-only** (no glyph), as before. Mirrors the
 * REQ-UI-069 alert valid-location gate in :file:`alert-layer-impl.ts` for that
 * residual case.
 *
 * Like the events source, the detections source is **not clustered** —
 * detections are sparse and each glyph must stay individually clickable for
 * the Map <-> Detections selection edge (REQ-UI-051 / ADR-024 §5).
 *
 * # Selection highlight
 *
 * A dedicated stroke-only ring layer, filtered to the selected ``report_id``
 * via :func:`setSelectedDetectionHighlight` (a ``setFilter`` call — no source
 * rebuild). The ring is the shared **action accent**
 * (:func:`selection-ring.getSelectionRingColor` — the ``--brand`` token,
 * amber in dark / blue in light) per storyboard Flow Q "action-color
 * selection ring", matching the platform ring + the event ring + the list
 * panels' ``bg-brand`` stripe (REQ-UI-040 / REQ-UI-051), so a selection
 * reads identically across entity kinds.
 *
 * Cross-references:
 *   - REQ-UI-068 (Detection map glyphs) / REQ-UI-046 (glyph-colour
 *     legibility) / REQ-UI-051 (Map <-> Detections selection) /
 *     REQ-UI-071 (range/bearing anchored at the reporting platform) /
 *     REQ-INGEST-044 (nullable position).
 *   - ADR-016 §4 (WebGL); ADR-024 §5 (selection contract);
 *     ADR-032 §4.1 (Detection data model).
 *   - map/alert-layer-impl.ts (the sibling point-glyph layer mirroring this
 *     canonical pattern).
 */
import type {
  Map as MapLibreMap,
  GeoJSONSource,
  FilterSpecification,
  ExpressionSpecification,
} from "maplibre-gl";
import { getDetectionGlyphColor, resolveSingleColor } from "@/map/marker-styling";
import {
  SELECTION_RING_STROKE_OPACITY,
  SELECTION_RING_STROKE_WIDTH,
  getSelectionRingColor,
  selectionRingRadiusExpression,
} from "@/map/selection-ring";
import {
  glyphIconImage,
  iconSizeExpression,
  normalizeGlyphId,
  registerGlyphIcons,
} from "@/map/glyphs";
import { isMapUsable } from "@/map/map-ready";
import { diffFeatureCollection, type FeatureSignatures } from "@/map/source-diff";
import type { DetectionRecord } from "@/detections/detections-store";
import { AGE_FADE_FACTORS, ageStepFor, composeAgeStepExpression } from "@/map/age-fade";
import {
  headOf,
  isPresentedTrack,
  memberTimeMs,
  type ObjectTrackRecord,
} from "@/object-tracks/object-track-store";

/** MapLibre GeoJSON source ID for the detections layer. */
export const DETECTIONS_SOURCE_ID = "meridian-detections-source";

/** MapLibre circle-layer ID for the detection glyphs. */
export const DETECTIONS_LAYER_ID = "meridian-detections-layer";

/** MapLibre circle-layer ID for the selected-detection highlight ring. */
export const DETECTIONS_HIGHLIGHT_LAYER_ID = "meridian-detections-highlight-layer";

/**
 * Sentinel report_id the highlight filter uses when nothing is selected —
 * a value no real SAPIENT report_id can equal, so the ring paints nothing.
 */
const HIGHLIGHT_NONE_SENTINEL = " __no_selected_detection__";

/**
 * Zoom -> glyph radius stops. Matches the event-glyph stops so detections
 * read as the same "ping" scale as events (slightly smaller than the
 * platform status markers).
 */
export const DETECTION_RADIUS_ZOOM_STOPS: ReadonlyArray<readonly [number, number]> = [
  [0, 3],
  [7, 5],
  [14, 7],
];

/**
 * Index of ``producer_id`` → ``[longitude_deg, latitude_deg]`` used to anchor
 * a null-position (unprojectable range/bearing) detection at its reporting
 * platform marker (REQ-UI-071). Built from the viewport-scoped snapshot in
 * :file:`Map.tsx` (:func:`map/snapshot-types.buildPlatformPositionIndex`), so
 * only on-screen platforms can anchor a detection — which is exactly when the
 * anchor would be visible.
 */
export type PlatformPositionIndex = ReadonlyMap<string, readonly [number, number]>;

/**
 * ``icon-opacity`` for a normally-located detection glyph (a precise WGS84
 * fix). Matches the pre-REQ-UI-071 flat opacity so located glyphs read
 * unchanged.
 */
export const DETECTION_LOCATED_GLYPH_OPACITY = 0.9;

/**
 * ``icon-opacity`` for a detection **anchored** at its reporting platform
 * (an *approximate* position — the range/bearing couldn't be projected). The
 * fainter paint is the at-a-glance "approximate, not a precise fix" affordance
 * (CLAUDE.md §8.1 operator-visibility). **Provisional** — the §5.11 visual pass
 * may replace/augment this with a halo or dashed ring; kept as a single
 * data-driven knob so that pass has one place to tune.
 */
export const DETECTION_ANCHORED_GLYPH_OPACITY = 0.45;

/**
 * The faintest a glyph may paint, however old it is.
 *
 * A detection inside the live window is removed from the map by the store's
 * **prune**, never by the paint: fading to invisibility would leave an
 * operator looking at a line whose head they cannot see, which is the defect
 * the head glyph was just restored to fix (CLAUDE.md §8.1 operator
 * visibility). Only the *anchored* (approximate) glyphs reach it — a located
 * glyph's stalest band still sits above it, which a unit case locks in.
 *
 * **Provisional**, like :data:`DETECTION_ANCHORED_GLYPH_OPACITY` beside it:
 * the §5.11 visual pass is where this number is settled, in both themes and
 * against a basemap.
 */
export const DETECTION_MIN_GLYPH_OPACITY = 0.25;

/**
 * Data-driven ``icon-opacity``: the approximate-vs-located distinction
 * (REQ-UI-071), **faded by the detection's age band** on the schedule the
 * track lines use (:mod:`map/age-fade`; REQ-UI-167's Statement has always said
 * the line fades "on the same schedule as detection glyphs", which was true of
 * neither until 2026-10-09).
 *
 * A feature lacking ``anchored`` (every located detection) evaluates
 * ``["get", "anchored"]`` to ``null`` ≠ ``true`` → the located opacity; one
 * lacking ``age_step`` (every fresh detection) reads as band 0 → factor 1, so
 * a fresh located glyph paints exactly as it did before the fade existed.
 * Static layer structure — both memberships ride the feature properties, so
 * MapLibre re-evaluates on ``setData`` with no layer churn.
 */
const DETECTION_ICON_OPACITY_EXPRESSION: ExpressionSpecification = [
  "max",
  DETECTION_MIN_GLYPH_OPACITY,
  [
    "*",
    [
      "case",
      ["==", ["get", "anchored"], true],
      DETECTION_ANCHORED_GLYPH_OPACITY,
      DETECTION_LOCATED_GLYPH_OPACITY,
    ],
    composeAgeStepExpression(AGE_FADE_FACTORS),
  ],
];

/**
 * The ``report_id``s the glyph layer must **hide** because they are non-head
 * members of an object track (ADR-041 D-4; REQ-UI-169): for every
 * ``track_id`` shared by two or more buffered detections, every detection but
 * the head — the max ``(time, report_id)``, the writer's own tie-break order
 * modulo hash — is hidden. A detection with an empty ``track_id`` or the only
 * one of its track is never hidden (fail-safe: an unknown track costs a
 * clutter of glyphs, never a missing one).
 *
 * Derived from the **detections** buffer, not the object-track store, so the
 * glyph rule cannot disagree with the glyphs it filters — both read the same
 * rows. The ring layer is NOT filtered, so selecting a mid-track detection
 * (from the Tracks panel) still rings it (REQ-UI-169).
 */
export function nonHeadReportIds(detections: readonly DetectionRecord[]): ReadonlySet<string> {
  const headByTrack = new Map<string, DetectionRecord>();
  const membersByTrack = new Map<string, DetectionRecord[]>();
  for (const detection of detections) {
    if (detection.track_id === "") {
      continue;
    }
    const members = membersByTrack.get(detection.track_id);
    if (members === undefined) {
      membersByTrack.set(detection.track_id, [detection]);
    } else {
      members.push(detection);
    }
    const head = headByTrack.get(detection.track_id);
    if (head === undefined || isNewerDetection(detection, head)) {
      headByTrack.set(detection.track_id, detection);
    }
  }
  const hidden = new Set<string>();
  for (const [trackId, members] of membersByTrack) {
    if (members.length < 2) {
      continue;
    }
    const head = headByTrack.get(trackId);
    for (const member of members) {
      if (member !== head) {
        hidden.add(member.report_id);
      }
    }
  }
  return hidden;
}

function isNewerDetection(candidate: DetectionRecord, incumbent: DetectionRecord): boolean {
  const dt = Date.parse(candidate.time) - Date.parse(incumbent.time);
  if (dt !== 0) {
    return dt > 0;
  }
  return candidate.report_id > incumbent.report_id;
}

/**
 * A map glyph the detections buffer does not hold, taken from an object
 * track's own member record (ADR-041 D-4; REQ-UI-168 / -169).
 *
 * ``TrackMember`` already carries everything a detection feature needs — the
 * ``report_id`` the selection bus and the highlight filter key on, a position,
 * and the confidence that rides along — so standing a head glyph back up costs
 * no new data, no new layer and no schema change.
 */
export interface SynthesizedGlyph {
  readonly report_id: string;
  readonly producer_id: string;
  readonly latitude_deg: number | null;
  readonly longitude_deg: number | null;
  readonly detection_confidence: number | null;
  /** The member's detection time (epoch ms) — the age fade's input. */
  readonly time_ms: number;
  /**
   * Set only for a **non-head** member carried for its ring (REQ-UI-169): the
   * glyph filter drops it, the unfiltered ring layer finds it.
   */
  readonly nonHead?: true;
}

/** What the glyph layer should hide, and what it must add. */
export interface TrackGlyphPlan {
  /** Buffered ``report_id``s to omit from the glyph source. */
  readonly hidden: ReadonlySet<string>;
  /** Glyphs the buffer cannot supply. */
  readonly extraGlyphs: readonly SynthesizedGlyph[];
}

/** The selected detection plus the track the store's reverse index puts it in. */
export interface SelectedTrackMember {
  readonly reportId: string;
  readonly trackId: string;
}

/** Project a track member into the synthesised-glyph shape. */
function glyphFromMember(
  track: ObjectTrackRecord,
  member: ObjectTrackRecord["members"][number],
  nonHead?: true,
): SynthesizedGlyph {
  return {
    report_id: member.report_id,
    producer_id: track.producer_id,
    latitude_deg: member.latitude_deg,
    longitude_deg: member.longitude_deg,
    detection_confidence: member.detection_confidence,
    time_ms: memberTimeMs(member),
    ...(nonHead === true ? { nonHead } : {}),
  };
}

/**
 * Decide the glyph set for the object tracks — **the track store is the
 * authority for its own head** (ADR-041 D-4; REQ-UI-168, whose Notes said
 * this from the start).
 *
 * # The defect this exists for
 *
 * A track's line comes from the object-track store, which keeps a track while
 * its head is inside the live window (10 min). Its head *glyph* was an
 * ordinary detection from the detections buffer, which is bounded by
 * :data:`detections-store.DETECTION_BUFFER_CAP` (500) and seeded 100 rows at a
 * time. At the rates this system actually sees, that cap — not the window — is
 * what removes a detection: ~26 s of history at the 2026-10-07 site's 19/s,
 * under a second of it at 60 tracks × 3 Hz. So **any track quiet for longer
 * than the buffer's horizon rendered a line with no head at all**, and quiet
 * tracks sort to the bottom of the Tracks list, which is exactly where the
 * operator found them (report 2026-10-09; 18 of 27 censused quiet tracks had
 * no glyph anywhere near their head).
 *
 * The latent twin, same cause: the head was computed *from the buffer*, so
 * with the true head evicted and an older member still held, that older
 * member was promoted and the glyph painted at a **stale position** — wrong
 * rather than absent, and a track with fewer than two buffered members was
 * never filtered at all.
 *
 * # What it decides
 *
 * For every **presented** track (two or more members — ADR-041 D-1) the head
 * is the store's newest member, *unless the buffer holds something newer* —
 * the buffer also takes the REST backfill, which the track feed may not have
 * caught up with, and painting an older glyph than the newest fix we hold
 * would be its own defect. Every other buffered member of that track is
 * hidden; when the head itself is not buffered, it is synthesised.
 *
 * A track the store does not know keeps the previous buffer-derived rule
 * (:func:`nonHeadReportIds`) — a lagging or pruned store degrades to the old
 * rendering, never to a missing glyph (CLAUDE.md §8.1). A track with one
 * member is not presented: it draws no line, so there is no head to pair and
 * its detection renders as REQ-UI-068 specifies.
 *
 * Finally, a **selected** member the buffer has lost is synthesised too,
 * flagged ``nonHead`` — REQ-UI-169 requires the ring at any selected
 * detection "whether or not the detection renders a glyph", which the
 * buffered-member ``keepReportIds`` path could not reach.
 *
 * Cost is O(buffered detections) + O(presented tracks) — the same order as the
 * rule it replaces, with no walk of every member of every track (feature spec
 * ``ui-track-load-headroom`` §2.4).
 */
export function trackGlyphPlan(
  detections: readonly DetectionRecord[],
  tracks: readonly ObjectTrackRecord[],
  selected?: SelectedTrackMember | null,
): TrackGlyphPlan {
  const bufferedByTrack = new Map<string, DetectionRecord[]>();
  for (const detection of detections) {
    if (detection.track_id === "") {
      continue;
    }
    const members = bufferedByTrack.get(detection.track_id);
    if (members === undefined) {
      bufferedByTrack.set(detection.track_id, [detection]);
    } else {
      members.push(detection);
    }
  }

  const hidden = new Set<string>();
  const extraGlyphs: SynthesizedGlyph[] = [];
  const decided = new Set<string>();

  for (const track of tracks) {
    if (!isPresentedTrack(track)) {
      continue;
    }
    const head = headOf(track);
    if (head === null) {
      continue;
    }
    decided.add(track.track_id);
    const buffered = bufferedByTrack.get(track.track_id) ?? [];
    let headId = head.report_id;
    let headTime = memberTimeMs(head);
    for (const detection of buffered) {
      const time = Date.parse(detection.time);
      if (time > headTime || (time === headTime && detection.report_id > headId)) {
        headId = detection.report_id;
        headTime = time;
      }
    }
    let headIsBuffered = false;
    for (const detection of buffered) {
      if (detection.report_id === headId) {
        headIsBuffered = true;
      } else {
        hidden.add(detection.report_id);
      }
    }
    if (!headIsBuffered) {
      extraGlyphs.push(glyphFromMember(track, head));
    }
  }

  // Tracks the store had nothing to say about keep the previous rule.
  const undecided = detections.filter(
    (detection) => detection.track_id !== "" && !decided.has(detection.track_id),
  );
  if (undecided.length > 0) {
    for (const reportId of nonHeadReportIds(undecided)) {
      hidden.add(reportId);
    }
  }

  // The selected member, when neither the buffer nor the heads above carry it.
  if (selected !== undefined && selected !== null) {
    const known =
      extraGlyphs.some((glyph) => glyph.report_id === selected.reportId) ||
      detections.some((detection) => detection.report_id === selected.reportId);
    if (!known) {
      const track = tracks.find((candidate) => candidate.track_id === selected.trackId);
      const member = track?.members.find((entry) => entry.report_id === selected.reportId);
      if (track !== undefined && member !== undefined) {
        extraGlyphs.push(glyphFromMember(track, member, true));
      }
    }
  }

  return { hidden, extraGlyphs };
}

/**
 * The glyph layer's static filter — every feature except those flagged
 * ``non_head`` (REQ-UI-169). Data-driven, so membership changes ride
 * ``setData`` with no filter churn. The highlight ring has its own filter.
 */
const GLYPH_LAYER_FILTER: FilterSpecification = ["!=", ["get", "non_head"], true];

/**
 * Convert the detections buffer into a GeoJSON point FeatureCollection.
 *
 * A **located** detection (non-null ``latitude_deg`` / ``longitude_deg``)
 * renders at its own WGS84 position with ``{ report_id, detection_confidence }``
 * properties (unchanged shape — no ``anchored`` key).
 *
 * A **null-position** detection (an unprojectable BSI range/bearing,
 * REQ-INGEST-044) is **anchored at its reporting platform marker** when
 * ``platformPositions`` knows that ``producer_id`` (REQ-UI-071): it renders at
 * the platform's ``[lng, lat]`` with an extra ``anchored: true`` property
 * (which drives the fainter "approximate" opacity). When the platform's
 * position is unknown (no ``platformPositions`` arg, or no entry for this
 * ``producer_id`` — the platform has no marker to anchor to), the detection is
 * **excluded** and stays list-only.
 *
 * @param detections - the detection buffer (located + unprojectable mixed).
 * @param platformPositions - ``producer_id`` → ``[lng, lat]`` anchor index
 *   (viewport-scoped; from the snapshot). Omit to disable anchoring (every
 *   null-position detection is then excluded — the pre-REQ-UI-071 behaviour).
 * @param hiddenReportIds - the non-head track members (:func:`nonHeadReportIds`;
 *   REQ-UI-169). They are **omitted** from the collection — the heads-only
 *   source of feature spec ``ui-track-load-headroom`` §2.2, so the worker lays
 *   out and the GPU draws only what is shown — except those in
 *   ``keepReportIds``. Omit for none.
 * @param keepReportIds - hidden members to carry anyway, flagged
 *   ``non_head: true`` so the glyph filter still drops them while the
 *   unfiltered ring layer can find them: the selected detection, so a
 *   selected mid-track member still rings (REQ-UI-169). Omit for none.
 */
export function detectionsToFeatureCollection(
  detections: readonly DetectionRecord[],
  platformPositions?: PlatformPositionIndex,
  hiddenReportIds?: ReadonlySet<string>,
  keepReportIds?: ReadonlySet<string>,
  glyphs?: GlyphInputs,
): GeoJSON.FeatureCollection<GeoJSON.Point> {
  const features: GeoJSON.Feature<GeoJSON.Point>[] = [];
  for (const detection of detections) {
    const hidden = hiddenReportIds?.has(detection.report_id) === true;
    if (hidden && keepReportIds?.has(detection.report_id) !== true) {
      continue;
    }
    const feature = glyphFeature(detection, hidden, platformPositions, glyphs);
    if (feature !== null) {
      features.push(feature);
    }
  }
  // Glyphs the buffer cannot supply — a presented track's head it has evicted,
  // and a selected member it has lost (:func:`trackGlyphPlan`). Placed through
  // the same builder as the buffered rows, so a synthesised head is
  // indistinguishable from a buffered one: same properties, same anchoring,
  // same age band, same selection and ring behaviour.
  for (const glyph of glyphs?.extraGlyphs ?? []) {
    const feature = glyphFeature(glyph, glyph.nonHead === true, platformPositions, glyphs);
    if (feature !== null) {
      features.push(feature);
    }
  }
  return { type: "FeatureCollection", features };
}

/**
 * What to draw beyond the buffered rows, and how old "now" makes everything.
 *
 * ``now`` / ``liveWindowMs`` are what the age fade is measured against
 * (ADR-041 D-7). Both optional: a caller that supplies neither gets every
 * glyph in the fresh band, which is how the layer behaved before the fade
 * existed and keeps the builder usable from a test that does not care.
 */
export interface GlyphInputs {
  /** Glyphs the detections buffer cannot supply (:func:`trackGlyphPlan`). */
  readonly extraGlyphs?: readonly SynthesizedGlyph[];
  /** The reference "now" (epoch ms) for the age band. */
  readonly now?: number;
  /** The deployment live window (ms) — the band fractions' denominator. */
  readonly liveWindowMs?: number;
}

/** The fields a glyph feature is built from — a buffered row or a synthesised one. */
interface GlyphSource {
  readonly report_id: string;
  readonly producer_id: string;
  readonly latitude_deg: number | null;
  readonly longitude_deg: number | null;
  readonly detection_confidence: number | null;
  /** Epoch ms, stamped at ingest by both stores; absent on a test literal. */
  readonly time_ms?: number;
  /** ISO-8601 — the fallback when the stamp is absent. */
  readonly time?: string;
}

/**
 * The feature's age band, or ``undefined`` for the fresh band (and whenever
 * the caller supplied no clock), so a fresh located feature's property shape
 * is unchanged. The stamped ``time_ms`` is preferred over parsing ``time``:
 * re-parsing a timestamp per row per render is the cost that dominated the
 * 2026-10-07 site wedge.
 */
function glyphAgeStep(source: GlyphSource, glyphs: GlyphInputs | undefined): number | undefined {
  if (glyphs?.now === undefined || glyphs.liveWindowMs === undefined) {
    return undefined;
  }
  const timeMs =
    source.time_ms ?? (source.time === undefined ? undefined : Date.parse(source.time));
  if (timeMs === undefined || Number.isNaN(timeMs)) {
    return undefined;
  }
  const step = ageStepFor(glyphs.now - timeMs, glyphs.liveWindowMs);
  return step === 0 ? undefined : step;
}

/**
 * One glyph feature, or ``null`` when the position is unknowable.
 *
 * ``report_id`` is the selection-bus id + the highlight filter key;
 * ``detection_confidence`` (nullable) rides along for hover/debug and any
 * future confidence-aware paint without a source rebuild. ``non_head``
 * (ADR-041) is only set when true so located features' shape is unchanged.
 * An unprojectable range/bearing fix (REQ-INGEST-044) anchors at the
 * reporting platform's marker when its position is known (REQ-UI-071) and is
 * flagged ``anchored`` for the fainter paint; otherwise it is list-only.
 */
function glyphFeature(
  source: GlyphSource,
  nonHead: boolean,
  platformPositions?: PlatformPositionIndex,
  glyphs?: GlyphInputs,
): GeoJSON.Feature<GeoJSON.Point> | null {
  const ageStep = glyphAgeStep(source, glyphs);
  const flags = {
    ...(nonHead ? { non_head: true } : {}),
    ...(ageStep === undefined ? {} : { age_step: ageStep }),
  };
  if (source.latitude_deg !== null && source.longitude_deg !== null) {
    return {
      type: "Feature",
      geometry: { type: "Point", coordinates: [source.longitude_deg, source.latitude_deg] },
      properties: {
        report_id: source.report_id,
        detection_confidence: source.detection_confidence,
        ...flags,
      },
    };
  }
  const anchor = platformPositions?.get(source.producer_id);
  if (anchor === undefined) {
    return null;
  }
  return {
    type: "Feature",
    geometry: { type: "Point", coordinates: [anchor[0], anchor[1]] },
    properties: {
      report_id: source.report_id,
      detection_confidence: source.detection_confidence,
      anchored: true,
      ...flags,
    },
  };
}

function highlightFilter(reportId: string | null): FilterSpecification {
  return ["==", ["get", "report_id"], reportId ?? HIGHLIGHT_NONE_SENTINEL];
}

/**
 * Previous-pass feature signatures per map — what the detections source
 * holds, keyed by ``report_id`` — so the next attach posts a diff instead of
 * reposting every glyph (feature spec ``ui-track-load-headroom`` §2.4;
 * REQ-UI-194). Cleared on detach.
 */
const detectionSignatures = new WeakMap<MapLibreMap, FeatureSignatures>();

function detectionFeatureId(feature: GeoJSON.Feature<GeoJSON.Point>): string {
  return String(feature.properties?.report_id);
}

/**
 * Register the source + glyph layer + highlight layer on the live map.
 * Idempotent — on first call adds them; on subsequent calls posts the
 * **diff** against what the source holds via :meth:`GeoJSONSource.updateData`
 * (no teardown, no repost of unchanged glyphs — REQ-UI-194), so detection
 * arrivals / expirations don't churn the layer (the cluster-flicker lesson
 * inherited from the event layer).
 *
 * ``singleColor`` is the active Profile's ``map.detection.single_color``
 * (inc6b; REQ-UI-087); ``glyph`` is its ``map.detection.glyph`` shape (GLYPHS
 * batch; default ``circle``). On first attach it registers the glyph SDF icons,
 * paints the symbol layer's ``icon-image`` (the chosen glyph) + ``icon-color``
 * (the colour); on the existing-source path (a data update, a theme flip, or a
 * Profile Apply) it re-applies the icon image + colour so a shape/colour change
 * repaints the live layer without a teardown. A blank/absent colour falls back
 * to the ``--detection-glyph`` token (:func:`getDetectionGlyphColor`); an
 * unknown glyph id falls back to ``circle`` (:func:`normalizeGlyphId`).
 *
 * ``platformPositions`` (``producer_id`` → ``[lng, lat]``; REQ-UI-071) anchors
 * null-position (unprojectable range/bearing) detections at their reporting
 * platform marker, painted fainter to read as approximate. Omit it (or omit a
 * given ``producer_id``) to keep those detections list-only.
 *
 * ``hiddenReportIds`` (:func:`nonHeadReportIds`; ADR-041 / REQ-UI-169) marks
 * a track's non-head members ``non_head`` so the glyph layer's filter drops
 * them — the line + one head glyph is a track's whole rendering — while the
 * ring layer, unfiltered, still rings a selected mid-track detection.
 */
export function attachDetectionLayer(
  map: MapLibreMap,
  detections: readonly DetectionRecord[],
  singleColor?: string,
  glyph?: string,
  platformPositions?: PlatformPositionIndex,
  plan?: TrackGlyphPlan,
  selectedReportId?: string | null,
  age?: { readonly now: number; readonly liveWindowMs: number },
): void {
  // No-op safely on a removed / not-ready map (teardown race) — see
  // :file:`map/map-ready.ts`; CLAUDE.md §8.1 fail-safe.
  if (!isMapUsable(map)) {
    return;
  }
  // ``platformPositions`` anchors null-position detections at their reporting
  // platform marker (REQ-UI-071); omitted → null-position detections excluded.
  // ``plan`` is :func:`trackGlyphPlan`'s decision: which buffered members to
  // omit, and which glyphs the buffer cannot supply at all (an evicted track
  // head, a selected member it has lost). ``selectedReportId`` is the one
  // hidden *buffered* member the heads-only source keeps (REQ-UI-169 — the
  // ring must find a selected mid-track member).
  const featureCollection = detectionsToFeatureCollection(
    detections,
    platformPositions,
    plan?.hidden,
    typeof selectedReportId === "string" && selectedReportId.length > 0
      ? new Set([selectedReportId])
      : undefined,
    { extraGlyphs: plan?.extraGlyphs, now: age?.now, liveWindowMs: age?.liveWindowMs },
  );

  // Resolve the operator-chosen colour, falling back to the design-system cyan
  // token (which the Profile schema also defaults to). The runtime CSS read in
  // the fallback means a theme flip re-paints with the right hue (REQ-UI-040).
  const glyphColor = resolveSingleColor(singleColor, getDetectionGlyphColor());
  // Resolve the glyph shape (unknown id → circle) and its registered SDF id.
  const glyphId = normalizeGlyphId(glyph);
  const iconImage = glyphIconImage(glyphId);

  // Ensure the glyph SDF icons exist before any symbol layer references them
  // (idempotent; safe regardless of map-load / attach ordering).
  registerGlyphIcons(map);

  const existing = map.getSource(DETECTIONS_SOURCE_ID) as GeoJSONSource | undefined;
  if (existing) {
    // Post the difference, not the collection: a frame that moved k heads
    // costs k updates (REQ-UI-194). The signatures are the previous pass's.
    const { diff, signatures } = diffFeatureCollection(
      detectionSignatures.get(map) ?? new Map(),
      featureCollection,
      detectionFeatureId,
    );
    detectionSignatures.set(map, signatures);
    if (diff !== null) {
      void existing.updateData(diff);
    }
    // Re-apply the glyph image + colour so a Profile shape/colour change or
    // theme flip repaints the live glyph in place (REQ-UI-087 / REQ-UI-040).
    // No-op before the layer is registered (defensive).
    if (map.getLayer(DETECTIONS_LAYER_ID) !== undefined) {
      map.setLayoutProperty(DETECTIONS_LAYER_ID, "icon-image", iconImage);
      map.setPaintProperty(DETECTIONS_LAYER_ID, "icon-color", glyphColor);
    }
    return;
  }

  // ``promoteId`` keys the source by ``report_id`` so ``updateData`` (the
  // existing-source path above) can address a feature by id.
  map.addSource(DETECTIONS_SOURCE_ID, {
    type: "geojson",
    data: featureCollection,
    promoteId: "report_id",
  });
  detectionSignatures.set(
    map,
    diffFeatureCollection(new Map(), featureCollection, detectionFeatureId).signatures,
  );
  map.addLayer({
    id: DETECTIONS_LAYER_ID,
    type: "symbol",
    source: DETECTIONS_SOURCE_ID,
    // Head-only glyphs per track (ADR-041 D-4; REQ-UI-169). Static; the
    // membership rides the feature property.
    filter: GLYPH_LAYER_FILTER,
    layout: {
      "icon-image": iconImage,
      "icon-size": iconSizeExpression(DETECTION_RADIUS_ZOOM_STOPS),
      // Sparse glyphs must always render (never collide away) — each must stay
      // individually clickable for the Map <-> Detections selection edge.
      "icon-allow-overlap": true,
      "icon-ignore-placement": true,
    },
    paint: {
      // SDF tint — the inc6b single colour (REQ-UI-087). ``icon-opacity`` is
      // data-driven (REQ-UI-071): full for located glyphs (matching the prior
      // flat opacity so they read the same), fainter for anchored/approximate.
      "icon-color": glyphColor,
      "icon-opacity": DETECTION_ICON_OPACITY_EXPRESSION,
    },
  });

  // Selection ring on top — stroke-only, filtered to the selected detection.
  map.addLayer({
    id: DETECTIONS_HIGHLIGHT_LAYER_ID,
    type: "circle",
    source: DETECTIONS_SOURCE_ID,
    filter: highlightFilter(null),
    paint: {
      "circle-radius": selectionRingRadiusExpression(DETECTION_RADIUS_ZOOM_STOPS),
      "circle-color": "#000000",
      "circle-opacity": 0,
      "circle-stroke-width": SELECTION_RING_STROKE_WIDTH,
      "circle-stroke-color": getSelectionRingColor(),
      "circle-stroke-opacity": SELECTION_RING_STROKE_OPACITY,
    },
  });
}

/**
 * Point the highlight ring at ``reportId`` (or clear it when ``null``) via a
 * ``setFilter`` call. No-op when the layer isn't registered yet.
 */
export function setSelectedDetectionHighlight(map: MapLibreMap, reportId: string | null): void {
  // No-op safely on a removed / not-ready map (teardown race) — see
  // :file:`map/map-ready.ts`.
  if (!isMapUsable(map)) {
    return;
  }
  if (!map.getLayer(DETECTIONS_HIGHLIGHT_LAYER_ID)) {
    return;
  }
  map.setFilter(DETECTIONS_HIGHLIGHT_LAYER_ID, highlightFilter(reportId));
}

/**
 * Tear down the highlight layer, glyph layer, and source on unmount (layers
 * before source, per MapLibre's ordering).
 */
export function detachDetectionLayer(map: MapLibreMap): void {
  // No-op safely on a removed / not-ready map (teardown race) — see
  // :file:`map/map-ready.ts`; CLAUDE.md §8.1 fail-safe.
  if (!isMapUsable(map)) {
    return;
  }
  detectionSignatures.delete(map);
  if (map.getLayer(DETECTIONS_HIGHLIGHT_LAYER_ID)) {
    map.removeLayer(DETECTIONS_HIGHLIGHT_LAYER_ID);
  }
  if (map.getLayer(DETECTIONS_LAYER_ID)) {
    map.removeLayer(DETECTIONS_LAYER_ID);
  }
  if (map.getSource(DETECTIONS_SOURCE_ID)) {
    map.removeSource(DETECTIONS_SOURCE_ID);
  }
}
